import React, { useState } from 'react';
import { User, Mail, Eye, EyeOff, CheckCircle, KeyRound } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import StatusBadge from '../../components/UI/StatusBadge';

const ROLE_LABELS = {
  admin:         { label: 'Administrator', tone: 'purple' },
  manager:       { label: 'Manager',       tone: 'teal'   },
  supervisor:    { label: 'Supervisor',    tone: 'info'   },
  gate_operator: { label: 'Gate Operator', tone: 'success' },
};

export default function ProfilePage() {
  const { user, updateProfile, changePassword } = useAuth();

  // Profile form
  const [profileForm, setProfileForm] = useState({ name: user?.name || '', email: user?.email || '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);

  // Password form
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [savingPw, setSavingPw] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const roleMeta = ROLE_LABELS[user?.role] || ROLE_LABELS.gate_operator;

  async function handleProfileSave(e) {
    e.preventDefault();
    if (!profileForm.name.trim() || !profileForm.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    setSavingProfile(true);
    try {
      const result = await updateProfile(profileForm);
      if (result.success) {
        setProfileSaved(true);
        setTimeout(() => setProfileSaved(false), 3000);
      }
    } finally {
      setSavingProfile(false);
    }
  }

  async function handlePasswordSave(e) {
    e.preventDefault();
    if (!pwForm.currentPassword || !pwForm.newPassword || !pwForm.confirmPassword) {
      toast.error('All password fields are required');
      return;
    }
    if (pwForm.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    setSavingPw(true);
    try {
      const result = await changePassword({
        currentPassword: pwForm.currentPassword,
        newPassword: pwForm.newPassword,
      });
      if (result.success) {
        setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      }
    } finally {
      setSavingPw(false);
    }
  }

  return (
    <div className="px-6 py-6 max-w-2xl mx-auto space-y-6">

      <PageHeader title="Profile" subtitle="Manage your account details and password" />

      {/* Account info card */}
      <Card padded={false} className="overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <User size={16} className="text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-700">Account Information</h2>
        </div>

        <div className="px-6 py-5 flex items-center gap-5">
          {/* Avatar */}
          <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
            <span className="text-2xl font-bold text-blue-700">
              {user?.name?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <p className="text-lg font-bold text-gray-900">{user?.name}</p>
            <p className="text-sm text-gray-500 flex items-center gap-1.5 mt-0.5">
              <Mail size={13} /> {user?.email}
            </p>
            <div className="mt-2">
              <StatusBadge label={roleMeta.label} tone={roleMeta.tone} />
            </div>
          </div>
        </div>
      </Card>

      {/* Edit profile form */}
      <Card padded={false} className="overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <User size={16} className="text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-700">Edit Profile</h2>
        </div>

        <form onSubmit={handleProfileSave} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={profileForm.name}
              onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Your full name"
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={profileForm.email}
              onChange={e => setProfileForm(f => ({ ...f, email: e.target.value }))}
              placeholder="your@email.com"
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end pt-1">
            <Button
              type="submit"
              variant="primary"
              icon={profileSaved ? CheckCircle : undefined}
              loading={savingProfile}
            >
              {savingProfile ? 'Saving…' : profileSaved ? 'Saved!' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </Card>

      {/* Change password form */}
      <Card padded={false} className="overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <KeyRound size={16} className="text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-700">Change Password</h2>
        </div>

        <form onSubmit={handlePasswordSave} className="px-6 py-5 space-y-4">
          {[
            { label: 'Current Password', key: 'currentPassword', show: showCurrent, toggle: setShowCurrent },
            { label: 'New Password',     key: 'newPassword',     show: showNew,     toggle: setShowNew,     hint: 'Minimum 6 characters' },
            { label: 'Confirm New Password', key: 'confirmPassword', show: showConfirm, toggle: setShowConfirm },
          ].map(({ label, key, show, toggle, hint }) => (
            <div key={key}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {label} <span className="text-red-500">*</span>
                {hint && <span className="ml-1 text-xs font-normal text-gray-400">({hint})</span>}
              </label>
              <div className="relative">
                <input
                  type={show ? 'text' : 'password'}
                  value={pwForm[key]}
                  onChange={e => setPwForm(f => ({ ...f, [key]: e.target.value }))}
                  placeholder="••••••••"
                  autoComplete={key === 'currentPassword' ? 'current-password' : 'new-password'}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => toggle(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {show ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
          ))}

          <div className="flex justify-end pt-1">
            <Button type="submit" variant="primary" loading={savingPw}>
              {savingPw ? 'Updating…' : 'Update Password'}
            </Button>
          </div>
        </form>
      </Card>

    </div>
  );
}
